import React, { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

const CountdownTimer: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const { t } = useLanguage();

  useEffect(() => {
    const launchDate = new Date('2025-06-01T00:00:00Z');

    const calculateTimeLeft = () => {
      const difference = +launchDate - +new Date();
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div 
      className="gradient-border blur-bg p-6 rounded-xl inline-flex flex-col sm:flex-row items-center space-y-4 sm:space-y-0 sm:space-x-8"
      role="timer"
      aria-label={t('launch.countdown')}
    >
      <div className="flex items-center">
        <Clock className="h-6 w-6 text-primary dark:text-primary-light mr-3" aria-hidden="true" />
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {t('launch.countdown')}
        </span>
      </div>
      <div className="flex space-x-4" role="list">
        <TimeUnit value={timeLeft.days} label={t('launch.days')} />
        <TimeUnit value={timeLeft.hours} label={t('launch.hours')} />
        <TimeUnit value={timeLeft.minutes} label={t('launch.minutes')} />
        <TimeUnit value={timeLeft.seconds} label={t('launch.seconds')} />
      </div>
    </div>
  );
};

interface TimeUnitProps {
  value: number;
  label: string;
}

const TimeUnit: React.FC<TimeUnitProps> = ({ value, label }) => (
  <div className="text-center min-w-[60px]" role="listitem">
    <div className="text-2xl font-bold gradient-text" aria-label={`${value} ${label}`}>
      {value.toString().padStart(2, '0')}
    </div>
    <div className="text-xs text-gray-600 dark:text-gray-400">{label}</div>
  </div>
);

export default CountdownTimer;